import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, Bell, TrendingUp, Smartphone } from "lucide-react";
import { ASSETS } from "@/lib/assets";

const NODE_SIZE = 96;
const RADIUS = 42; // percent from center

// Sense/Understand/Prompt/Learn are the sensor + app experience WearSpray is developing.
// Apply is the one node backed by the real, shipping, patented product \u2014 kept visually
// and label-distinct so the diagram never implies the concept stages already exist today.
// All 5 nodes share the same circular footprint (icon or photo) so the pentagon geometry
// stays symmetric at every breakpoint \u2014 a taller/narrower node shape previously caused
// collisions with its neighbors on mobile.
const NODES = [
  {
    n: "01",
    label: "Sense",
    angle: -90,
    kind: "icon",
    icon: Radio,
    today: false,
    detail: "Reads context like UV, activity and environment \u2014 in development.",
  },
  {
    n: "02",
    label: "Understand",
    angle: -18,
    kind: "icon",
    icon: Smartphone,
    today: false,
    detail: "Interprets personal routines through the companion app \u2014 in development.",
  },
  {
    n: "03",
    label: "Prompt",
    angle: 54,
    kind: "icon",
    icon: Bell,
    today: false,
    detail: "Suggests the right moment to apply \u2014 in development.",
  },
  {
    n: "04",
    label: "Apply",
    angle: 126,
    kind: "photo",
    img: ASSETS.fingerPressReservoir,
    today: true,
    detail: "The patented, refillable wearable \u2014 available today.",
  },
  {
    n: "05",
    label: "Learn",
    angle: 198,
    kind: "icon",
    icon: TrendingUp,
    today: false,
    detail: "Adapts guidance over time \u2014 in development.",
  },
];

function polar(angleDeg) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: 50 + RADIUS * Math.cos(rad), y: 50 + RADIUS * Math.sin(rad) };
}

function LoopNode({ node, index, isHovered, onHover, onLeave }) {
  const pos = polar(node.angle);
  const glow = isHovered
    ? "0 0 42px rgba(0,229,255,0.75)"
    : ["0 0 16px rgba(0,229,255,0.18)", "0 0 30px rgba(0,229,255,0.4)", "0 0 16px rgba(0,229,255,0.18)"];
  const Icon = node.icon;
  return (
    <div
      className="absolute flex flex-col items-center gap-2 cursor-pointer"
      style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: "translate(-50%, -50%)" }}
      data-testid={`solution-loop-node-${node.n}`}
      onMouseEnter={() => onHover(node.n)}
      onMouseLeave={onLeave}
      onFocus={() => onHover(node.n)}
      onBlur={onLeave}
      tabIndex={0}
    >
      {node.kind === "photo" ? (
        <motion.img
          src={node.img}
          alt={node.label}
          className="rounded-full object-cover border-2 border-accent/70"
          style={{ width: NODE_SIZE, height: NODE_SIZE, boxShadow: isHovered ? glow : undefined }}
          animate={isHovered ? { scale: 1.14, boxShadow: glow } : { scale: 1, boxShadow: glow }}
          transition={isHovered ? { duration: 0.25, ease: "easeOut" } : { duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: index * 0.3 }}
        />
      ) : (
        <motion.div
          className="rounded-full flex items-center justify-center border-2 border-accent/70 bg-black/60 text-accent"
          style={{ width: NODE_SIZE, height: NODE_SIZE, boxShadow: isHovered ? glow : undefined }}
          animate={isHovered ? { scale: 1.14, boxShadow: glow } : { scale: 1, boxShadow: glow }}
          transition={isHovered ? { duration: 0.25, ease: "easeOut" } : { duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: index * 0.3 }}
        >
          <Icon size={30} strokeWidth={1.5} />
        </motion.div>
      )}
      <span className="font-mono text-[10px] sm:text-sm tracking-[0.1em] uppercase text-zinc-300 whitespace-nowrap">
        {node.n} {"\u00b7"} {node.label}
      </span>
      <span
        className={`font-mono text-[7px] sm:text-[8px] tracking-[0.08em] uppercase px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap ${
          node.today ? "bg-accent text-black" : "text-zinc-500 border border-zinc-700"
        }`}
        data-testid={`solution-loop-badge-${node.n}`}
      >
        {node.today ? "Today" : "Concept"}
      </span>
    </div>
  );
}

export function SolutionLoop() {
  const [hovered, setHovered] = useState(null);
  const active = NODES.find((node) => node.n === hovered);

  return (
    <div
      className="relative aspect-square w-[320px] sm:w-[400px] md:w-[440px] lg:w-[480px] mx-auto"
      data-testid="solution-loop"
    >
      <motion.svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 100 100"
        animate={{ rotate: 360 }}
        transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
      >
        <circle cx="50" cy="50" r="45" fill="none" stroke="#00e5ff" strokeOpacity="0.3" strokeWidth="0.8" strokeDasharray="3 6" />
      </motion.svg>

      <div className="absolute inset-0 flex items-center justify-center px-10" data-testid="solution-loop-detail">
        <AnimatePresence mode="wait">
          {active ? (
            <motion.div
              key={active.n}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.18 }}
              className="text-center w-28 sm:w-36 md:w-40 lg:w-44"
            >
              <p className="font-mono text-xs tracking-[0.15em] uppercase text-accent">
                {active.n} {"\u00b7"} {active.label}
              </p>
              <p className="font-body text-xs sm:text-sm text-zinc-200 leading-snug mt-2">{active.detail}</p>
            </motion.div>
          ) : (
            <motion.span
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 text-center leading-relaxed"
            >
              Closed
              <br />
              Loop
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {NODES.map((node, i) => (
        <LoopNode
          key={node.n}
          node={node}
          index={i}
          isHovered={hovered === node.n}
          onHover={setHovered}
          onLeave={() => setHovered(null)}
        />
      ))}
    </div>
  );
}
